import { useEffect, useState, type ComponentProps } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Star,
  ExternalLink,
  Settings2,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
  RotateCcw,
  Eye,
} from 'lucide-react';
import {
  useJob,
  useSourcingMatches,
  useUpdateSourcingSettings,
  useSubmitSourcingFeedback,
  useForceReview,
} from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import { SkeletonDetail } from '../components/ui/Skeleton';
import SourcingCandidateDrawer from '../components/sourcing/SourcingCandidateDrawer';
import { TuningBanner } from '../components/sourcing/TuningBanner';
import { RankChipBadge } from '../components/sourcing/RankChipBadge';
import type { SourcingMatch } from '../types';
import { STAR_LABELS } from '../constants/sourcing-star-bands';

/**
 * Derived from the drawer's own prop type rather than re-declared. Finding I2
 * was exactly a re-declared shape drifting from its consumer: the table learned
 * about `aiFailed`, this local interface did not, and the drawer silently
 * rendered cosine stars labelled "AI Score" for candidates nothing had
 * assessed. Deriving it makes that class of drift a compile error.
 */
type DrawerCandidate = NonNullable<
  ComponentProps<typeof SourcingCandidateDrawer>['candidate']
>;


const PAGE_LIMIT = 20;

function StarRating({ stars }: { stars: number }) {
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          data-testid={n <= stars ? 'star-filled' : 'star-empty'}
          className={`h-3.5 w-3.5 ${n <= stars ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}
        />
      ))}
    </span>
  );
}

export default function SourcingPage() {
  const { id } = useParams<{ id: string }>();
  const jobId = parseInt(id || '0', 10);
  const { showToast } = useToast();

  const { data: job, isLoading: jobLoading } = useJob(jobId);
  const [localEnabled, setLocalEnabled] = useState<boolean | undefined>(undefined);
  const [localMinStars, setLocalMinStars] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(0);

  const enabled = localEnabled ?? job?.passiveSourcingEnabled ?? false;
  const minStars = localMinStars ?? job?.sourcingMinStars ?? 4;

  // US-403 (C2): this is the one surface with real pagination, so it is the one
  // that has to hold the spec version. Page 1 is fetched WITHOUT specVersion
  // (there's nothing to be stale against yet) and learns the current version
  // from the response; every later page sends it back, so the server can tell
  // us the ranking changed underneath us instead of silently serving a page
  // from a different ordering — which duplicates some candidates and skips
  // others.
  const [pinnedSpecVersion, setPinnedSpecVersion] = useState<number | null>(null);

  const { data, isLoading: matchesLoading, error: matchesError } = useSourcingMatches(jobId, {
    limit: PAGE_LIMIT,
    offset: page * PAGE_LIMIT,
    minStars,
    specVersion: page > 0 && pinnedSpecVersion !== null ? pinnedSpecVersion : undefined,
  });

  useEffect(() => {
    if (data?.specVersion != null) setPinnedSpecVersion(data.specVersion);
  }, [data?.specVersion]);

  useEffect(() => {
    const err = matchesError as (Error & { statusCode?: number; body?: { currentSpecVersion?: number | null } }) | null;
    if (!err || err.statusCode !== 409) return;
    // Criteria changed mid-page. Re-sync to the version the 409 body reports
    // and restart from page 1 — the only page that is meaningful under a
    // ranking the client has never seen.
    setPinnedSpecVersion(err.body?.currentSpecVersion ?? null);
    setPage(0);
    showToast('Search criteria changed — showing refreshed results', 'success');
  }, [matchesError, showToast]);

  const updateSettings = useUpdateSourcingSettings();
  const submitFeedback = useSubmitSourcingFeedback();
  const forceReview = useForceReview();

  // Item 9: a failed screening is only actionable once. Track which rows have
  // already been re-queued so the button reflects that instead of inviting a
  // recruiter to fire the same job repeatedly while it sits in the queue.
  const [retriedApplicationIds, setRetriedApplicationIds] = useState<Set<number>>(new Set());

  const handleRetryScreening = async (applicationId: number) => {
    try {
      await forceReview.mutateAsync(applicationId);
      setRetriedApplicationIds((prev) => new Set(prev).add(applicationId));
      showToast('AI screening re-queued', 'success');
    } catch {
      showToast('Failed to re-queue AI screening', 'error');
    }
  };

  const [hiddenCandidateIds, setHiddenCandidateIds] = useState<Set<number>>(new Set());
  const [drawerCandidate, setDrawerCandidate] = useState<DrawerCandidate | null>(null);

  const parseReviewList = (text: string | null): string[] =>
    text
      ? text
          .split('\n')
          .map((line) => line.replace(/^-\s*/, '').trim())
          .filter(Boolean)
      : [];

  const toDrawerCandidate = (match: SourcingMatch): DrawerCandidate => ({
    candidateId: match.candidateId,
    jobId,
    candidateName: match.candidateName,
    candidateEmail: match.candidateEmail,
    score: match.score,
    stars: match.stars,
    aiPending: match.aiPending ?? false,
    aiFailed: match.aiFailed ?? false,
    applicationId: match.applicationId ?? null,
    aiReview:
      match.aiPending || match.aiFailed || match.overallScore == null
        ? null
        : {
            overallScore: match.overallScore,
            strengths: parseReviewList(match.strengths),
            concerns: parseReviewList(match.concerns),
          },
    feedback: match.feedback ?? null,
  });

  const hideCandidate = (candidateId: number) =>
    setHiddenCandidateIds((prev) => {
      const next = new Set(prev);
      next.add(candidateId);
      return next;
    });

  const handleReject = async (candidateId: number) => {
    try {
      await submitFeedback.mutateAsync({ jobId, candidateId, feedback: 'rejected' });
      hideCandidate(candidateId);
    } catch {
      showToast('Failed to submit feedback', 'error');
    }
  };

  const handleApprove = async (candidateId: number) => {
    try {
      await submitFeedback.mutateAsync({ jobId, candidateId, feedback: 'approved' });
    } catch {
      showToast('Failed to submit feedback', 'error');
    }
  };

  const handleToggle = async (val: boolean) => {
    try {
      await updateSettings.mutateAsync({ jobId, data: { passiveSourcingEnabled: val } });
      setLocalEnabled(val);
      showToast(val ? 'Passive sourcing enabled' : 'Passive sourcing disabled', 'success');
    } catch {
      showToast('Failed to update settings', 'error');
    }
  };

  const handleMinStars = async (stars: number) => {
    try {
      await updateSettings.mutateAsync({ jobId, data: { sourcingMinStars: stars } });
      setLocalMinStars(stars);
      setPage(0);
      showToast('Minimum match updated', 'success');
    } catch {
      showToast('Failed to update settings', 'error');
    }
  };

  if (jobLoading) {
    return <div role="status"><SkeletonDetail /></div>;
  }

  const totalPages = data ? Math.ceil(data.total / PAGE_LIMIT) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to={`/jobs/${jobId}`} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Suggested Candidates</h2>
          {job?.title && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{job.title}</p>
          )}
        </div>
      </div>

      {/* US-509: the same ribbon the Suggested tab shows, from the same
          response. This page is that tab's "View all →" destination, so a
          recruiter following the link was losing the only explanation of why
          the list is ordered the way it is.

          `tuned` is derived from `specVersion`, NOT from `data.tuned`, for the
          reason documented in SuggestedCandidates: `data.tuned` says whether
          THIS response's ranking ran spec-aware, and goes false whenever the
          inference budget is exhausted or ranking degrades — none of which mean
          the job reverted to uncalibrated. `specVersion` is non-null exactly
          when an applied spec exists, which is the persistent fact the banner
          is describing.

          Gated on `data` so the untuned "rate 12 candidates" call to action
          never flashes before we know whether this job is tuned. */}
      {enabled && data && (
        <TuningBanner
          jobId={jobId}
          tuned={data.specVersion != null}
          termCounts={{ mustHave: data.mustHaveCount, exclusions: data.exclusionCount }}
          // US-510: both of these come from the response. `tunedAt` is the
          // applied spec's created_at; passing null here (as this call site
          // originally did) made the banner print "recently" — a claim with no
          // source. `rankingApplied` is the response's own `tuned` flag, which
          // is the ONLY thing that says whether the order in front of the
          // recruiter actually came from the criteria.
          tunedAt={data.tunedAt}
          rankingApplied={data.tuned}
        />
      )}

      {/* Settings card */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <Settings2 className="h-4 w-4 text-gray-400" />
          <label className="flex items-center gap-2 text-sm">
            <button
              role="switch"
              aria-checked={enabled}
              onClick={() => handleToggle(!enabled)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${enabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}
              disabled={updateSettings.isPending}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-4' : 'translate-x-1'}`} />
            </button>
            <span className="text-gray-700 dark:text-gray-300">Passive sourcing</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            Min match:
            <select
              value={minStars}
              onChange={(e) => handleMinStars(parseInt(e.target.value, 10))}
              className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1 text-xs"
              disabled={updateSettings.isPending}
            >
              {[5, 4, 3, 2, 1].map((s) => (
                <option key={s} value={s}>{STAR_LABELS[s]}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* Content */}
      {!enabled ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 py-16 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>Enable passive sourcing above to start finding suggested candidates for this role.</p>
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Sourcing runs nightly and caches results for 30 days.</p>
        </div>
      ) : matchesLoading ? (
        <div role="status" className="animate-pulse space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1 space-y-1">
                <div className="h-3 w-48 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-32 rounded bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="h-3 w-12 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          ))}
        </div>
      ) : !data?.matches.length ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 py-16 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>No suggested candidates yet.</p>
          <p className="mt-1 text-xs">Sourcing runs nightly — check back tomorrow.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Candidate</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Match</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Stars</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Expires</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.matches
                .filter((match) => !hiddenCandidateIds.has(match.candidateId))
                .map((match) => (
                <tr key={match.candidateId} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                  <td className="px-6 py-4">
                    {/* Item 10: link-blue text alone reads as "navigates
                        away". The eye glyph plus the "Preview" label name the
                        actual behaviour — a panel slides out and the list
                        stays put. */}
                    <button
                      type="button"
                      onClick={() => setDrawerCandidate(toDrawerCandidate(match))}
                      aria-label={`Preview ${match.candidateName}`}
                      title="Opens a preview panel"
                      className="group inline-flex items-center gap-1.5 text-left text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                    >
                      <Eye
                        data-testid="drawer-affordance-icon"
                        aria-hidden="true"
                        className="h-3.5 w-3.5 shrink-0 text-blue-500/70 dark:text-blue-400/70 group-hover:text-blue-600 dark:group-hover:text-blue-300"
                      />
                      {match.candidateName}
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{match.candidateEmail}</p>
                    {/* US-509: why this candidate sits where it does. Each chip
                        names a term from the recruiter's OWN search spec that
                        moved this row up or down — a different thing from the
                        Match column (cosine retrieval) and from the drawer's AI
                        score (reviews.overall_score). Rendered only from
                        `match.chips`, which the API sends; nothing is inferred
                        client-side, so a chip can never name a term that did
                        not actually drive an adjustment. */}
                    {match.chips?.length ? (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {match.chips.map((chip) => (
                          <RankChipBadge key={`${chip.direction}:${chip.term}`} chip={chip} />
                        ))}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{Math.round(match.score * 100)}%</td>
                  <td className="px-6 py-4">
                    {match.aiFailed ? (
                      // Item 9: honest terminal state. Static (never pulsing),
                      // amber rather than the neutral grey of "working on it",
                      // and readable in both themes.
                      <div className="flex items-center gap-2">
                        <span
                          data-testid="ai-failed-badge"
                          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
                        >
                          <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                          AI screening failed
                        </span>
                        {match.applicationId != null &&
                          (retriedApplicationIds.has(match.applicationId) ? (
                            <span className="text-xs text-gray-500 dark:text-gray-400">Re-queued</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleRetryScreening(match.applicationId!)}
                              disabled={forceReview.isPending}
                              aria-label={`Retry AI screening for ${match.candidateName}`}
                              className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40"
                            >
                              <RotateCcw className="h-3 w-3" aria-hidden="true" />
                              Retry
                            </button>
                          ))}
                      </div>
                    ) : match.aiPending ? (
                      <div
                        data-testid="ai-pending-skeleton"
                        role="status"
                        aria-label="AI screening in progress"
                        className="h-3.5 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700"
                      />
                    ) : (
                      <StarRating stars={match.stars} />
                    )}
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400">
                    {new Date(match.expiresAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => handleApprove(match.candidateId)}
                        disabled={submitFeedback.isPending}
                        aria-label="Approve"
                        {...(match.feedback === 'approved'
                          ? { 'data-testid': 'feedback-approved-active' }
                          : {})}
                        className={`inline-flex items-center rounded p-1 disabled:opacity-40 ${
                          match.feedback === 'approved'
                            ? 'text-green-500 dark:text-green-400'
                            : 'text-gray-400 hover:text-green-500 dark:hover:text-green-400'
                        }`}
                      >
                        <ThumbsUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReject(match.candidateId)}
                        disabled={submitFeedback.isPending}
                        aria-label="Reject"
                        {...(match.feedback === 'rejected'
                          ? { 'data-testid': 'feedback-rejected-active' }
                          : {})}
                        className={`inline-flex items-center rounded p-1 disabled:opacity-40 ${
                          match.feedback === 'rejected'
                            ? 'text-red-500 dark:text-red-400'
                            : 'text-gray-400 hover:text-red-500 dark:hover:text-red-400'
                        }`}
                      >
                        <ThumbsDown className="h-4 w-4" />
                      </button>
                      <Link
                        to={`/people/${match.candidateId}`}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400"
                      >
                        View
                      </Link>
                      {match.srCandidateId && (
                        <a
                          href={`https://www.smartrecruiters.com/app/people/candidates/${match.srCandidateId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400"
                          title="View in SmartRecruiters"
                        >
                          SR <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-6 py-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {data.total} suggestions total
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                  className="text-sm px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                  className="text-sm px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <SourcingCandidateDrawer
        candidate={drawerCandidate}
        onClose={() => setDrawerCandidate(null)}
        onFeedbackSubmit={(candidateId, feedback) => {
          if (feedback === 'rejected') {
            hideCandidate(candidateId);
          }
          setDrawerCandidate(null);
        }}
        // Same handler and same retried-set as the table row, so a retry fired
        // from either surface is reflected in both.
        onRetryScreening={handleRetryScreening}
        retryQueued={
          drawerCandidate?.applicationId != null &&
          retriedApplicationIds.has(drawerCandidate.applicationId)
        }
        retryPending={forceReview.isPending}
      />
    </div>
  );
}
